import React, { useEffect, useMemo, useState } from 'react';
import TopGainersView from './TopGainersView';
import TopLosersView from './TopLosersView';
import TopVolumeView from './TopVolumeView';
import TopAdxTrendView from './TopAdxTrendView';
import TrendCombinationView from './TrendCombinationView';
import { buildScopedApiUrl, ensureArray, getApiHeaders } from './config';
import { useWorkspace } from './workspace/WorkspaceContext';
import DashboardSymbolScopeBar from './DashboardSymbolScopeBar';
import useDashboardSymbolScope from './useDashboardSymbolScope';
import './FreeAnalyticsPage.css';

export const FREE_ANALYTICS_PAGES = {
  'top-gainers': { title: 'Top Gainers', endpoint: 'top-gainers', View: TopGainersView },
  'top-losers': { title: 'Top Losers', endpoint: 'top-losers', View: TopLosersView },
  'top-volume': { title: 'Top Volume', endpoint: 'top-volume', View: TopVolumeView },
  trend: { title: 'Trend Dashboard', endpoint: 'trend', View: TrendCombinationView },
  adx: { title: 'Top ADX Trend', endpoint: 'adx', View: TopAdxTrendView },
};

export default function FreeAnalyticsPage({ kind }) {
  const { workspace } = useWorkspace();
  const config = FREE_ANALYTICS_PAGES[kind];
  const symbolScope = useDashboardSymbolScope(workspace);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const visibleData = useMemo(() => kind === 'trend' && symbolScope.active ? data.filter(row => symbolScope.symbols.includes(row.symbol)) : data, [data, kind, symbolScope.active, symbolScope.symbols]);

  useEffect(() => {
    if (!config) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetch(buildScopedApiUrl(`/api/bot/ui/analytics/${config.endpoint}`, workspace), {
      headers: getApiHeaders(), signal: controller.signal, cache: 'no-store',
    })
      .then(async response => {
        if (!response.ok) throw new Error(`Unable to load ${config.title} (${response.status})`);
        return response.json();
      })
      .then(rows => setData(ensureArray(rows)))
      .catch(reason => {
        if (reason.name !== 'AbortError') setError(reason.message || `Unable to load ${config.title}`);
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [config, workspace]);

  if (!config) return <div className="analytics-page-message analytics-page-message--error">Unknown analytics page.</div>;
  if (loading) return <div className="analytics-page-message">Loading {config.title}...</div>;
  if (error) return <div className="analytics-page-message analytics-page-message--error">{error}</div>;
  const View = config.View;
  return (
    <section className="free-analytics-page" aria-label={config.title}>
      <header className="free-analytics-page__header">
        <span>FREE MARKET ANALYTICS</span><h1>{config.title}</h1>
        <p>{workspace.exchange} / {workspace.marketType} - {visibleData.length} closed-candle rows</p>
      </header>
      {kind === 'trend' && <DashboardSymbolScopeBar scope={symbolScope}/>} 
      {visibleData.length === 0
        ? <div className="analytics-page-message">No closed-candle analytics are available for this workspace yet.</div>
        : <View data={visibleData} />}
    </section>
  );
}
